const ESTIMATE_WINDOW_FRAMES = 45;
const MINIMUM_OBSERVED_FRAMES = 3;
const ESTIMATE_SMOOTHING = 0.25;

type FrameTimeSample = {
  completed: number;
  timestampMs: number;
};

export class ExportFrameTimeEstimator {
  private samples: FrameTimeSample[] = [];
  private millisecondsPerFrame: number | null = null;

  update(completedFrames: number, totalFrames: number, timestampMs = getNowMs()): number | null {
    if (
      !Number.isFinite(completedFrames) ||
      !Number.isFinite(totalFrames) ||
      !Number.isFinite(timestampMs)
    ) {
      return null;
    }
    const total = Math.max(0, Math.floor(totalFrames));
    const completed = Math.max(0, Math.min(total, Math.floor(completedFrames)));
    if (total <= 0 || completed <= 0) {
      return null;
    }
    if (completed >= total) {
      return 0;
    }

    // The first sample is taken after frame one. Asset loading and decoder
    // startup therefore do not inflate the estimate for all remaining frames.
    const lastSample = this.samples[this.samples.length - 1];
    if (!lastSample || completed > lastSample.completed) {
      this.samples.push({ completed, timestampMs });
      this.trimSamples(completed);
      this.updateFrameDuration();
    }

    if (!this.millisecondsPerFrame) {
      return null;
    }
    return ((total - completed) * this.millisecondsPerFrame) / 1000;
  }

  private trimSamples(completedFrames: number): void {
    while (
      this.samples.length > 2 &&
      completedFrames - this.samples[0].completed > ESTIMATE_WINDOW_FRAMES
    ) {
      this.samples.shift();
    }
  }

  private updateFrameDuration(): void {
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    const observedFrames = last.completed - first.completed;
    const observedMs = last.timestampMs - first.timestampMs;
    if (observedFrames < MINIMUM_OBSERVED_FRAMES || observedMs <= 0) {
      return;
    }

    const observedMsPerFrame = observedMs / observedFrames;
    this.millisecondsPerFrame = this.millisecondsPerFrame === null
      ? observedMsPerFrame
      : this.millisecondsPerFrame * (1 - ESTIMATE_SMOOTHING) + observedMsPerFrame * ESTIMATE_SMOOTHING;
  }
}

export function formatEstimatedTimeRemaining(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) {
    return 'Estimating time...';
  }
  if (seconds <= 0) {
    return 'Finishing render...';
  }

  const roundedSeconds = Math.max(1, Math.ceil(seconds));
  if (roundedSeconds < 60) {
    return `Estimated time remaining: ${roundedSeconds}s`;
  }

  const totalMinutes = Math.floor(roundedSeconds / 60);
  const remainingSeconds = roundedSeconds % 60;
  if (totalMinutes < 60) {
    return `Estimated time remaining: ${totalMinutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `Estimated time remaining: ${hours}h ${minutes}m`;
}

function getNowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
